import * as Sentry from '@sentry/react';
import api from '../../common/api/api';

// Untyped like the Hasura rows the screens already handle.
type Row = any;
type Page = { items: Row[]; has_more: boolean };

// Hasura had no limit: page at the API max until has_more is false (MAX_OFFSET 10k).
const PAGE = 1000;
const MAX_OFFSET = 10000;

type Params = URLSearchParams | Record<string, unknown>;

export async function getData(path: string, params?: Params): Promise<any> {
  const res = await api.get(path, params);
  if (!res.success || !res.data) throw new Error(`${path} ${res.status ?? ''}`.trim());
  return res.data;
}

export async function getAll(path: string, params = new URLSearchParams(), pageSize = PAGE): Promise<Row[]> {
  const items: Row[] = [];
  for (let offset = 0; ; offset += pageSize) {
    const query = new URLSearchParams(params);
    query.set('limit', String(pageSize));
    query.set('offset', String(offset));
    // Pages are sequential: has_more decides whether there is a next one.
    // eslint-disable-next-line no-await-in-loop
    const page: Page = await getData(path, query);
    items.push(...page.items);
    if (!page.has_more) break;
    if (offset + pageSize > MAX_OFFSET) {
      // The API caps offset: the list is cut, say so instead of hiding it.
      Sentry.captureMessage('restApi: list truncated at MAX_OFFSET', { level: 'warning', extra: { path } });
      break;
    }
  }
  return items;
}

// Ids are encoded so a value from the URL can't walk to another API path.
export const botPath = (botId: string, sub = '') => `bots/${encodeURIComponent(botId)}${sub}`;
export const orgPath = (orgId: string, sub = '') => `organizations/${encodeURIComponent(orgId)}${sub}`;

type WriteMethod = 'post' | 'put' | 'patch' | 'delete';

// Ola 3 writes: the stored row (or {deleted}) comes back; anything else throws with the status.
export async function writeData(method: WriteMethod, path: string, body?: unknown): Promise<any> {
  const res = method === 'delete' ? await api.delete(path) : await api[method](path, body);
  if (!res.success) throw new Error(`${method.toUpperCase()} ${path} ${res.status ?? ''}`.trim());
  return res.data;
}
